import { useState } from 'react';
import { useInvalidateMyQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface CancelQuoteRequestInput {
  readonly quoteRequestId: string;
}

export interface UseCancelQuoteRequestResult {
  /** 성공하면 true. 확인 창은 호출부가 띄운다. */
  readonly cancel: (input: CancelQuoteRequestInput) => Promise<boolean>;
  readonly isPending: boolean;
  readonly error: string | null;
}

const ERROR_FALLBACK = '요청 취소에 실패했습니다.';

/**
 * 요청 취소(quotes.cancelRequest, 견적 설계 §7-8) — 성공하면 상세·목록을 다시 불러온다.
 * 상태 충돌(CONFLICT, 그 사이 발행·반려 등)이면 문구와 함께 상세·목록을 다시 불러와 낡은 버튼을 걷어 낸다.
 */
export const useCancelQuoteRequest = (): UseCancelQuoteRequestResult => {
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateMyQuotes();
  const mutation = trpc.quotes.cancelRequest.useMutation();

  const cancel = async (input: CancelQuoteRequestInput): Promise<boolean> => {
    setError(null);

    try {
      await mutation.mutateAsync(input);
      await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      return true;
    } catch (caught) {
      setError(getErrorMessage(caught, ERROR_FALLBACK));

      if (getTrpcErrorCode(caught) === 'CONFLICT') {
        await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      }

      return false;
    }
  };

  return { cancel, isPending: mutation.isPending, error };
};
